import { Box, Container, Fade, Toolbar } from "@mui/material";
import React, { Suspense, lazy, useCallback, useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Routes as RouterRoutes, Route } from "react-router-dom";
import PropTypes from "prop-types";

import CustomAppBar from "../components/AppBar";
import Drawer from "../components/Drawer";
import Loading from "../components/Loading";
import UpdateDetailsDialog from "../components/UpdateDetailsDialog";

const routes = [
  {
    exact: true,
    path: "/",
    component: lazy(() => import("../views/Feed")),
  },
  {
    exact: true,
    path: "/forgot_password",
    component: lazy(() => import("../views/ForgotPassword")),
  },
  {
    exact: true,
    path: "/activities/:id",
    component: lazy(() => import("../views/Activity")),
  },
  {
    exact: true,
    path: "/programmes/:id",
    component: lazy(() => import("../views/Programme")),
  },
  {
    exact: true,
    path: "/summary_of_service",
    component: lazy(() => import("../views/SummaryOfService")),
    authGuarded: true,
  },
  {
    exact: true,
    path: "/search",
    component: lazy(() => import("../views/Search")),
  },
  {
    exact: true,
    path: "/completed",
    component: lazy(() => import("../views/Completed")),
    authGuarded: true,
  },
  {
    exact: true,
    path: "/interested",
    component: lazy(() => import("../views/Interested")),
    authGuarded: true,
  },
  {
    exact: true,
    path: "/Sustainable-Development-Goals",
    component: lazy(() => import("../views/SustainableDevelopmentGoals")),
  },
  {
    exact: true,
    path: "/contact_us",
    component: lazy(() => import("../views/ContactSVA")),
    authGuarded: true,
  },

  {
    exact: true,
    path: "/my_teams",
    component: lazy(() => import("../views/MyTeams")),
    authGuarded: true,
  },
  {
    exact: true,
    path: "/teams/:id",
    component: lazy(() => import("../views/Team")),
    authGuarded: true,
  },
  {
    exact: true,
    path: "/teams/:id/members",
    component: lazy(() => import("../views/TeamMembers")),
    authGuarded: true,
  },
  {
    exact: true,
    path: "/upcoming",
    component: lazy(() => import("../views/Upcoming")),
    authGuarded: true,
  },
  {
    exact: true,
    path: "/notifications",
    component: lazy(() => import("../views/Notifications")),
    authGuarded: true,
  },
  {
    exact: true,
    path: "/profile",
    component: lazy(() => import("../views/Profile")),
    authGuarded: true,
  },
  {
    exact: true,
    path: "/complete-profile",
    component: lazy(() => import("../views/CompleteProfile")),
    authGuarded: true,
    standalone: true, // No sidebar, no appbar - user must complete profile
  },
  {
    exact: true,
    path: "/verify-activity/:token",
    component: lazy(() => import("../views/VerifyActivity")),
    standalone: true, // No login required, standalone page for verifiers
  },
  {
    exact: true,
    path: "*",
    component: lazy(() => import("../views/404")),
  },
];

const devRoutes = [
  {
    exact: true,
    path: "/storybook",
    component: lazy(() => import("../views/Storybook")),
  },
  ...routes,
];

function AuthGuard({ isAuthed = false, children, isCompleteProfilePage = false }) {
  const navigate = useNavigate();
  const profile = useSelector((state) => state.profile);
  const { id, volunteer_details, teams } = profile;
  const { displaySignIn } = useSelector((state) => state.style);

  useEffect(() => {
    // If auth is required but user is not logged in, redirect to home
    if (isAuthed && !id && displaySignIn) {
      navigate("/");
      return;
    }

    // Check if profile is complete by verifying required fields
    const hasInstitution = volunteer_details?.institution_id || profile.institution?.id;
    const hasTeams = teams && teams.length > 0;
    const hasPhone = !!profile.phone;
    const hasDob = !!volunteer_details?.dob;

    const isProfileComplete = hasInstitution && hasTeams && hasPhone && hasDob;

    // If user is logged in but profile is not completed, redirect to complete-profile
    // (but not if we're already on the complete-profile page)
    if (id && !isProfileComplete && !isCompleteProfilePage) {
      navigate("/complete-profile");
      return;
    }

    // If user is on complete-profile page but profile is already completed, redirect to home
    if (isCompleteProfilePage && isProfileComplete) {
      navigate("/");
    }
  }, [id, profile, volunteer_details, teams, isAuthed, navigate, displaySignIn, isCompleteProfilePage]);

  return children;
}

AuthGuard.propTypes = {
  children: PropTypes.node.isRequired,
  isAuthed: PropTypes.bool,
  isCompleteProfilePage: PropTypes.bool,
};

export default function Routes() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const { id } = useSelector((state) => state.profile);
  const { displaySignIn } = useSelector((state) => state.style);

  const drawerWidth = useMemo(() => (id ? 325 : 0), [id]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleCloseDrawer = useCallback(() => setMobileOpen(false), []);

  const boxStyle = useMemo(
    () => ({
      flexGrow: 1,
      pt: 3,
      pl: { md: 3 },
      marginLeft: { md: `${drawerWidth}px` },
      marginTop: id || !displaySignIn ? 0 : "350px",
      minHeight: "100vh",
      overflow: "hidden",
    }),
    [id, displaySignIn, drawerWidth]
  );

  const containerStyle = useMemo(() => ({ bgcolor: "background.paper" }), []);

  return (
    <Suspense fallback={<Loading />}>
      <RouterRoutes>
        {(process.env.NODE_ENV === "production" ? routes : devRoutes).map((route) => {
          const Component = route.component;

          // Standalone pages (like complete-profile) don't show sidebar or appbar
          if (route.standalone) {
            // Public standalone pages (no authGuarded) don't need AuthGuard
            if (!route.authGuarded) {
              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<Component />}
                />
              );
            }
            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <AuthGuard isAuthed={route.authGuarded} isCompleteProfilePage={route.path === "/complete-profile"}>
                    <Component />
                  </AuthGuard>
                }
              />
            );
          }

          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                <AuthGuard isAuthed={route.authGuarded}>
                  <CustomAppBar handleCloseDrawer={handleCloseDrawer} handleDrawerToggle={handleDrawerToggle} />
                  <Fade in exit>
                    <Container maxWidth={false} sx={containerStyle}>
                      <Container maxWidth="lg" sx={{ position: "relative" }}>
                        <Drawer
                          path={route.path}
                          drawerWidth={drawerWidth}
                          mobileOpen={mobileOpen}
                          handleDrawerToggle={handleDrawerToggle}
                          handleCloseDrawer={handleCloseDrawer}
                        />
                        <Box component="main" sx={boxStyle}>
                          <Toolbar />
                          <Component />
                        </Box>
                      </Container>
                    </Container>
                  </Fade>
                  <UpdateDetailsDialog />
                </AuthGuard>
              }
            />
          );
        })}
      </RouterRoutes>
    </Suspense>
  );
}
